import axios from 'axios';

const resolvedBaseURL =
  process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:4000'
    : 'https://rga-backend-app.fly.dev');

// Create an Axios instance with credentials enabled so cookies are sent
const api = axios.create({
  baseURL: resolvedBaseURL,
  withCredentials: true
});

// Request interceptor to attach the access token
api.interceptors.request.use((config) => {
  // Skip authentication for login and password recovery endpoints
  const skipAuthUrls = [
    '/user/login',
    '/user/forgot-password',
    '/user/reset-password',
    '/user/refresh',
    '/qdf-report/public/'
  ];
  const shouldSkipAuth = skipAuthUrls.some(url => config.url?.includes(url));

  if (!shouldSkipAuth) {
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No access token found in localStorage');
    }
  }

  // For FormData, let the browser set Content-Type with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isPublicAuthRoute =
      originalRequest?.url?.includes('/user/login') ||
      originalRequest?.url?.includes('/user/forgot-password') ||
      originalRequest?.url?.includes('/user/reset-password') ||
      originalRequest?.url?.includes('/user/refresh');

    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403) &&
      !originalRequest._retry &&
      !isPublicAuthRoute
    ) {
      originalRequest._retry = true;
      try {
        const { data } = await api.post('/user/refresh', {}, { withCredentials: true });

        // Keep the token where login put it: localStorage only for "remember me".
        const storage = localStorage.getItem('userId') ? localStorage : sessionStorage;
        storage.setItem('accessToken', data.accessToken);

        // Update request with new token
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userId');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('userId');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// QDF Report API functions
export const qdfAPI = {
  // Create a new QDF report
  createReport: (formData) => {
    return api.post('/qdf-report', formData);
  },

  // Get all QDF reports with optional filters
  getReports: (params = {}) => {
    return api.get('/qdf-report', { params });
  },

  // Get a specific QDF report by ID
  getReport: (id) => {
    return api.get(`/qdf-report/${id}`);
  },

  // Update a QDF report
  updateReport: (id, formData) => {
    return api.put(`/qdf-report/${id}`, formData);
  },

  // Delete a QDF report
  deleteReport: (id) => {
    return api.delete(`/qdf-report/${id}`);
  },

  // Get QDF statistics
  getStats: () => {
    return api.get('/qdf-report/stats/overview');
  },

  // Get QDF monthly summary for closed PM/SR records
  getMonthlySummary: (params = {}) => {
    return api.get('/qdf-report/monthly-summary', { params });
  },

  // Generate Monthly PM work orders for all zones for a given month (admin only)
  generateMonthlyPMs: (data = {}) => {
    return api.post('/qdf-report/generate-monthly-pms', data);
  },

  // Public QR: verify token and retrieve locked prefill data
  verifyPublicToken: (token) => {
    return api.get(`/qdf-report/public/verify/${encodeURIComponent(token)}`);
  },

  // Public QR: submit a corrective service request for a verified token
  submitPublicReport: (token, payload) => {
    return api.post(`/qdf-report/public/submit/${encodeURIComponent(token)}`, payload);
  },

  // Shops settings
  getShops: () => {
    return api.get('/qdf-report/shops');
  },

  bootstrapShops: (payload = {}) => {
    return api.post('/qdf-report/shops/bootstrap', payload);
  },

  createShop: (payload) => {
    return api.post('/qdf-report/shops', payload);
  },

  updateShop: (id, payload) => {
    return api.put(`/qdf-report/shops/${id}`, payload);
  },

  deleteShop: (id) => {
    return api.delete(`/qdf-report/shops/${id}`);
  }
};

export const monthlyReportAPI = {
  generateMonthlyReport: (data = {}) => {
    return api.post('/monthly-report/generated/generate', data);
  },

  getGeneratedMonthlyReports: (params = {}) => {
    return api.get('/monthly-report/generated', { params });
  },

  getGeneratedMonthlyReport: (id) => {
    return api.get(`/monthly-report/generated/${id}`);
  }
};

export const attendanceAPI = {
  getToday: () => {
    return api.get('/attendance/today');
  },

  checkIn: (data = {}) => {
    return api.post('/attendance/check-in', data);
  },

  checkOut: (data = {}) => {
    return api.post('/attendance/check-out', data);
  },

  reportAbsence: (data = {}) => {
    return api.post('/attendance/report-absence', data);
  },

  getHistory: (params = {}) => {
    return api.get('/attendance/history', { params });
  },

  getMissedCheckoutRequests: (params = {}) => {
    return api.get('/attendance/missed-checkout-requests', { params });
  },

  submitMissedCheckoutRequest: (data = {}) => {
    return api.post('/attendance/missed-checkout-request', data);
  },

  approveMissedCheckoutRequest: (id, data = {}) => {
    return api.post(`/attendance/missed-checkout-requests/${id}/approve`, data);
  },

  rejectMissedCheckoutRequest: (id, data = {}) => {
    return api.post(`/attendance/missed-checkout-requests/${id}/reject`, data);
  },

  resolveReviewFlagsBulk: (data = {}) => {
    return api.post('/attendance/review/resolve-bulk', data);
  },

  exportHistoryCsv: (params = {}) => {
    return api.get('/attendance/export-csv', {
      params,
      responseType: 'blob'
    });
  },

  updateAttendance: (id, data = {}) => {
    return api.put(`/attendance/${id}`, data);
  },

  deleteAttendance: (id) => {
    return api.delete(`/attendance/${id}`);
  }
};

export const rosterAPI = {
  getProfiles: () => api.get('/rosters/profiles'),
  saveProfile: (userId, profile) => api.put(`/rosters/profiles/${userId}`, profile),
  getMonth: (month) => api.get(`/rosters/${month}`),
  generate: (month) => api.post('/rosters/generate', { month }),
  save: (roster) => api.put(`/rosters/${roster.month}`, { rows: roster.rows, version: roster.version, neighborVersions: roster.neighborVersions }),
};
